import { useCallback, useEffect, useState } from 'react';
import { api, ErroApi, NaoAutenticado, type Sessao } from './api';
import { useRota, idDetalhe } from './rota';
import { TabBar } from './componentes/TabBar';
import { Entrada } from './telas/Entrada';
import { Carteira } from './telas/Carteira';
import { MeuCodigo } from './telas/MeuCodigo';
import { Lojinha } from './telas/Lojinha';
import { Detalhe } from './telas/Detalhe';
import { Cupons, CupomCheio } from './telas/Cupons';
import { Extrato } from './telas/Extrato';
import { Credito } from './telas/Credito';
import { Perfil } from './telas/Perfil';
import { Carregando } from '../../compartilhado/Espera';

/**
 * O porteiro do app: sem sessão, a Entrada; com sessão, as telas do mockup.
 * A máquina de estados é a mesma do painel — carregando / fora / dentro.
 */
type Estado = 'carregando' | 'fora' | 'dentro';

export function App() {
  const { caminho } = useRota();
  const [estado, setEstado] = useState<Estado>('carregando');
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [codigoAberto, setCodigoAberto] = useState(false);
  const [avisoQr, setAvisoQr] = useState('');

  const carregarSessao = useCallback(() => {
    api
      .sessao()
      .then((s) => {
        setSessao(s);
        setEstado('dentro');
      })
      .catch(() => setEstado('fora'));
  }, []);

  useEffect(() => {
    /*
     * O QR do balcão (21/08): ?qr=token na URL vira sessão antes de qualquer
     * tela. O token some da barra em seguida — ele é de uso único, e URL
     * compartilhada por engano não pode carregar autoridade nenhuma.
     */
    const token = new URLSearchParams(location.search).get('qr');
    if (!token) return void carregarSessao();
    api
      .entrarQr(token)
      .catch((e) => setAvisoQr(e instanceof ErroApi ? e.message : 'não consegui entrar pelo QR'))
      .finally(() => {
        history.replaceState(null, '', location.pathname + location.hash);
        carregarSessao();
      });
  }, [carregarSessao]);

  /* 401 em qualquer tela derruba para a Entrada — cookie venceu. */
  const aoFalhar = useCallback((e: unknown) => {
    if (e instanceof NaoAutenticado) setEstado('fora');
  }, []);

  /* A primeira abertura do app: não há nada na tela para cobrir, então a esfera
     fica sozinha no vão. Era um "…" cinza — o painel girava a marca e o app
     mostrava reticências. */
  if (estado === 'carregando')
    return (
      <div className="espera-tela">
        <Carregando />
      </div>
    );
  if (estado === 'fora' || !sessao) return <Entrada aoEntrar={carregarSessao} aviso={avisoQr || undefined} />;

  const idRecompensa = idDetalhe(caminho, 'recompensa');
  const codigoCupom = idDetalhe(caminho, 'cupom');

  /* O cupom cheio toma a tela inteira, sem abas — é para mostrar no caixa. */
  if (codigoCupom)
    return <CupomCheio codigo={codigoCupom} sessao={sessao} aoFalhar={aoFalhar} />;

  const tela = idRecompensa ? (
    <Detalhe id={idRecompensa} aoFalhar={aoFalhar} />
  ) : caminho === '/lojinha' ? (
    <Lojinha aoFalhar={aoFalhar} />
  ) : caminho === '/cupons' ? (
    <Cupons sessao={sessao} aoFalhar={aoFalhar} />
  ) : caminho === '/extrato' ? (
    <Extrato aoFalhar={aoFalhar} />
  ) : caminho === '/credito' ? (
    <Credito aoFalhar={aoFalhar} />
  ) : caminho === '/perfil' ? (
    <Perfil sessao={sessao} aoSair={() => setEstado('fora')} />
  ) : (
    <Carteira sessao={sessao} aoFalhar={aoFalhar} abrirCodigo={() => setCodigoAberto(true)} />
  );

  return (
    <div className="moldura">
      <div className="conteudo">{tela}</div>
      {/* O extrato e o crédito são telas de leitura, com o voltar próprio: a
          barra de baixo ali seria dois jeitos de sair da mesma tela. */}
      {!idRecompensa && caminho !== '/extrato' && caminho !== '/credito' && <TabBar />}
      {codigoAberto && <MeuCodigo sessao={sessao} fechar={() => setCodigoAberto(false)} />}
    </div>
  );
}
